"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, Layers, Globe as Globe2, Network, CircleHelp as HelpCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
interface ArchitectureOption {
  id: string;
  name: string;
  urlFormat: string;
  icon: React.ElementType;
  summary: string;
  authoritySignal: string;
  pros: string[];
  cons: string[];
  recommendedFor: string;
}
const architectureOptions: ArchitectureOption[] = [{
  id: "cctld",
  name: "ccTLD (Country Code Top-Level Domain)",
  urlFormat: "yoursite.de / yoursite.fr",
  icon: Globe2,
  summary: "A dedicated domain for each target country. This provides the clearest country signal to users and search engines, but requires building authority independently for each domain.",
  authoritySignal: "Separate domain authority per country domain",
  pros: ["Clearest country signal for search engines and local users", "Higher initial trust and click-through rates from regional searchers who prefer local domains", "Enables independent hosting configurations and distinct legal entities per country"],
  cons: ["Splits backlink equity across independent domains, requiring separate link building for each", "Higher registration, SSL, and technical maintenance overhead across multiple domains", "Requires substantial resources to establish organic authority from scratch on each site"],
  recommendedFor: "Businesses with established brands, larger budgets, and dedicated local marketing operations in each country."
}, {
  id: "subdirectories",
  name: "Subdirectories (Subfolders)",
  urlFormat: "yoursite.com/de/ / yoursite.com/fr/",
  icon: Layers,
  summary: "All international variations reside in subfolders on a single root domain, using hreflang markup and crawlable navigation to define regional targets.",
  authoritySignal: "Shared domain authority across all market folders",
  pros: ["Shares root domain authority, backlinks, and historical trust across all international folders", "Simpler and more cost-effective to operate within a centralized content management system", "Unified technical infrastructure, single domain administration, and consolidated reporting"],
  cons: ["Less geographic separation than a native ccTLD for users who look for local domain extensions", "Requires careful hreflang configuration to prevent cross-folder cannibalisation", "Catalogue and navigation changes must be managed carefully across shared templates"],
  recommendedFor: "Businesses wanting to leverage their existing domain authority to enter new markets efficiently."
}, {
  id: "subdomains",
  name: "Subdomains",
  urlFormat: "de.yoursite.com / fr.yoursite.com",
  icon: Network,
  summary: "Regional instances configured on subdomains of the main domain. They sit technically between ccTLDs and subdirectories.",
  authoritySignal: "Treated more separately by search engines than subdirectories",
  pros: ["Allows different CMS platforms, servers, or regional software stacks per market", "Maintains brand cohesion while offering separate server routing for distinct technical setups", "Straightforward access control for regional engineering or marketing teams"],
  cons: ["Search engines treat subdomains more separately, so authority does not flow as seamlessly as in subdirectories", "Additional DNS, SSL certificate, and tracking configuration across each subdomain", "More complex internal linking and canonical maintenance between regional instances"],
  recommendedFor: "Organisations with separate regional software platforms, decoupled CMS setups, or technical constraints."
}];
export default function ArchitectureComparison() {
  return <section id="internationalarchitecturecomparison" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Editorial Header */}
        <div className="max-w-3xl mx-auto md:mx-0 mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4 text-xs tracking-wider uppercase font-semibold text-primary bg-secondary px-3 py-1">
              Architecture &amp; URL Structure
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
              International Website Architecture
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              No single URL structure is universally best for every business. Country-code domains (ccTLDs),
              subdirectories, and subdomains each have distinct technical, operational, and commercial trade-offs. I help
              clients choose the right model based on their existing domain authority, technical stack, operational
              capacity, available budget, brand recognition, and whether they sell a unified product catalogue or
              divergent regional ranges. Your URL architecture also shapes site navigation, internal linking structures,
              and catalogue discovery across target markets.
            </p>
          </motion.div>
        </div>

        {/* 3-Column Comparison Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {architectureOptions.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={index} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.12
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full border-border bg-card rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="p-2.5 rounded-lg bg-secondary text-primary">
                          <Icon className="size-5" />
                        </div>
                        <span className="font-mono text-xs px-2.5 py-1 rounded bg-muted text-muted-foreground font-medium border border-border">
                          {item.urlFormat}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-semibold tracking-tight text-card-foreground">
                        <h3 className="text-xl font-semibold tracking-tight text-card-foreground">{item.name}</h3>
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-5">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.summary}
                      </p>

                      <div className="p-3 rounded-lg bg-muted/60 border border-border/70 text-xs">
                        <span className="font-medium text-foreground block mb-0.5">
                          Authority Distribution:
                        </span>
                        <span className="text-muted-foreground font-mono">
                          {item.authoritySignal}
                        </span>
                      </div>

                      <Separator className="bg-border" />

                      {/* Pros */}
                      <div className="space-y-2.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary block">
                          Key Advantages
                        </span>
                        <ul className="space-y-2 text-xs sm:text-sm text-card-foreground">
                          {item.pros.map((pro, pIdx) => <li key={pIdx} className="flex items-start gap-2">
                              <Check className="size-4 text-chart-4 shrink-0 mt-0.5" />
                              <span className="leading-snug">{pro}</span>
                            </li>)}
                        </ul>
                      </div>

                      <Separator className="bg-border" />

                      {/* Cons */}
                      <div className="space-y-2.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                          Trade-offs &amp; Constraints
                        </span>
                        <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                          {item.cons.map((con, cIdx) => <li key={cIdx} className="flex items-start gap-2">
                              <X className="size-4 text-destructive/80 shrink-0 mt-0.5" />
                              <span className="leading-snug">{con}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <div className="p-6 pt-2">
                    <Separator className="bg-border mb-4" />
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground font-medium block mb-1">
                        Best Application:
                      </strong>
                      {item.recommendedFor}
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Strategic Decision Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="rounded-2xl border border-border bg-muted/40 p-6 md:p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-3xl space-y-2">
            <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
              <HelpCircle className="size-4" />
              <span>How I evaluate your setup</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
              Need clarity on your international SEO architecture?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              In an initial review, I evaluate your existing domain authority, CMS capabilities, internal linking, and
              operational resources to recommend an architecture that balances organic growth potential with practical
              maintenance.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}